import type { CancelablePromise } from "./core/CancelablePromise"
import { OpenAPI } from "./core/OpenAPI"
import { request as __request } from "./core/request"

import type {
  Body_login_login_access_token,
  Token,
  Message,
  UpdatePassword,
  UserCreate,
  UserPublic,
  UserRegister,
  UsersPublic,
  UserUpdate,
  UserUpdateMe,
  ItemCreate,
  ItemPublic,
  ItemsPublic,
  ItemUpdate,
  CartItemCreate,
  CartSchema,
  CheckoutForm,
  OrderPublic,
  PricerRequest,
  PricerResponse,
  OrderCreate,
  OrdersPublic,
  OrderUpdate,
  PingResponse,
  DocumentCreate,
  DocumentPublic,
  DocumentsPublic,
  DocumentUpdate,
  SignatureForm,
  Body_templates_create_template,
  Body_templates_create_template_link,
  Body_templates_patch_template,
  TemplatesPublic,
} from "./models"

export type LoginData = {
  LoginAccessToken: {
    formData: Body_login_login_access_token
  }
}

export type UsersData = {
  ReadUsers: {
    limit?: number
    skip?: number
  }
  CreateUser: {
    requestBody: UserCreate
  }
  UpdateUserMe: {
    requestBody: UserUpdateMe
  }
  UpdatePasswordMe: {
    requestBody: UpdatePassword
  }
  RegisterUser: {
    requestBody: UserRegister
  }
  ReadUserById: {
    userId: string
  }
  UpdateUser: {
    requestBody: UserUpdate
    userId: string
  }
  DeleteUser: {
    userId: string
  }
}

export type ItemsData = {
  ReadItems: {
    limit?: number
    skip?: number
  }
  CreateItem: {
    requestBody: ItemCreate
  }
  ReadItem: {
    id: string
  }
  UpdateItem: {
    id: string
    requestBody: ItemUpdate
  }
  DeleteItem: {
    id: string
  }
}

export type CartData = {
  AddToCart: {
    requestBody: CartItemCreate
  }
  DeleteCartItem: {
    id: string
  }
  OrderInfo: {
    requestBody: CheckoutForm
  }
  Checkout: {
    orderid: string
  }
  GetPrice: {
    requestBody: PricerRequest
  }
}

export type OrdersData = {
  GetOrders: {
    limit?: number
    skip?: number
  }
  CreateOrder: {
    requestBody: OrderCreate
  }
  PatchOrder: {
    orderId: string
    requestBody: OrderUpdate
  }
  DeleteOrder: {
    orderId: string
  }
}

export type EmailData = {
  TestEmail: {
    emailTo: string
  }
}

export type PingData = {}

export type DocumentsData = {
  CreateDocument: {
    requestBody: DocumentCreate
    templateName: string
  }
  GetDraftDocument: {
    orderId: string
  }
  SignDraftDocument: {
    orderId: string
    requestBody: SignatureForm
  }
  GetDocuments: {
    limit?: number
    skip?: number
  }
  PatchDocument: {
    documentId: string
    requestBody: DocumentUpdate
  }
  DeleteDocument: {
    documentId: string
  }
}

export type TemplateData = {
  CreateDocument: {
    requestBody: DocumentCreate
    templateName: string
  }
  GetTemplateFileByName: {
    templateName: string
  }
  GetTemplates: {
    limit?: number
    skip?: number
  }
  CreateTemplate: {
    formData: Body_templates_create_template
  }
  PatchTemplate: {
    formData: Body_templates_patch_template
    templateId: string
  }
  DeleteTemplate: {
    templateId: string
  }
  CreateTemplateLink: {
    formData: Body_templates_create_template_link
  }
}

export type DocumentData = {
  GetDraftDocument: {
    orderId: string
  }
  SignDraftDocument: {
    orderId: string
    requestBody: SignatureForm
  }
  GetDocuments: {
    limit?: number
    skip?: number
  }
  PatchDocument: {
    documentId: string
    requestBody: DocumentUpdate
  }
  DeleteDocument: {
    documentId: string
  }
}

export type TemplatesData = {
  GetTemplateFileByName: {
    templateName: string
  }
  GetTemplates: {
    limit?: number
    skip?: number
  }
  CreateTemplate: {
    formData: Body_templates_create_template
  }
  PatchTemplate: {
    formData: Body_templates_patch_template
    templateId: string
  }
  DeleteTemplate: {
    templateId: string
  }
  CreateTemplateLink: {
    formData: Body_templates_create_template_link
  }
}

export type PaymentsData = {
  Success: {
    sessionId: string
  }
  Cancel: {
    sessionId: string
  }
}

export type PaymentData = {
  Success: {
    sessionId: string
  }
  Cancel: {
    sessionId: string
  }
}

export class LoginService {
  /**
   * Login Access Token
   * OAuth2 compatible token login, get an access token for future requests
   * @returns Token Successful Response
   * @throws ApiError
   */
  public static loginAccessToken(
    data: LoginData["LoginAccessToken"],
  ): CancelablePromise<Token> {
    const { formData } = data
    return __request(OpenAPI, {
      method: "POST",
      url: "/api/v1/login/access-token",
      formData: formData,
      mediaType: "application/x-www-form-urlencoded",
      errors: {
        422: `Validation Error`,
      },
    })
  }
}

export class UsersService {
  /**
   * Read Users
   * Retrieve users.
   * @returns UsersPublic Successful Response
   * @throws ApiError
   */
  public static readUsers(
    data: UsersData["ReadUsers"] = {},
  ): CancelablePromise<UsersPublic> {
    const { skip = 0, limit = 100 } = data
    return __request(OpenAPI, {
      method: "GET",
      url: "/api/v1/users/",
      query: {
        skip,
        limit,
      },
      errors: {
        422: `Validation Error`,
      },
    })
  }

  /**
   * Create User
   * Create new user.
   * @returns UserPublic Successful Response
   * @throws ApiError
   */
  public static createUser(
    data: UsersData["CreateUser"],
  ): CancelablePromise<UserPublic> {
    const { requestBody } = data
    return __request(OpenAPI, {
      method: "POST",
      url: "/api/v1/users/",
      body: requestBody,
      mediaType: "application/json",
      errors: {
        422: `Validation Error`,
      },
    })
  }

  /**
   * Read User Me
   * Get current user.
   * @returns UserPublic Successful Response
   * @throws ApiError
   */
  public static readUserMe(): CancelablePromise<UserPublic> {
    return __request(OpenAPI, {
      method: "GET",
      url: "/api/v1/users/me",
    })
  }

  /**
   * Update User Me
   * Update own user.
   * @returns UserPublic Successful Response
   * @throws ApiError
   */
  public static updateUserMe(
    data: UsersData["UpdateUserMe"],
  ): CancelablePromise<UserPublic> {
    const { requestBody } = data
    return __request(OpenAPI, {
      method: "PATCH",
      url: "/api/v1/users/me",
      body: requestBody,
      mediaType: "application/json",
      errors: {
        422: `Validation Error`,
      },
    })
  }

  /**
   * Update Password Me
   * Update own password.
   * @returns Message Successful Response
   * @throws ApiError
   */
  public static updatePasswordMe(
    data: UsersData["UpdatePasswordMe"],
  ): CancelablePromise<Message> {
    const { requestBody } = data
    return __request(OpenAPI, {
      method: "PATCH",
      url: "/api/v1/users/me/password",
      body: requestBody,
      mediaType: "application/json",
      errors: {
        422: `Validation Error`,
      },
    })
  }

  /**
   * Register User
   * Create new user without the need to be logged in.
   * @returns UserPublic Successful Response
   * @throws ApiError
   */
  public static registerUser(
    data: UsersData["RegisterUser"],
  ): CancelablePromise<UserPublic> {
    const { requestBody } = data
    return __request(OpenAPI, {
      method: "POST",
      url: "/api/v1/users/signup",
      body: requestBody,
      mediaType: "application/json",
      errors: {
        422: `Validation Error`,
      },
    })
  }

  /**
   * Read User By Id
   * Get a specific user by id.
   * @returns UserPublic Successful Response
   * @throws ApiError
   */
  public static readUserById(
    data: UsersData["ReadUserById"],
  ): CancelablePromise<UserPublic> {
    const { userId } = data
    return __request(OpenAPI, {
      method: "GET",
      url: "/api/v1/users/{user_id}",
      path: {
        user_id: userId,
      },
      errors: {
        422: `Validation Error`,
      },
    })
  }

  /**
   * Update User
   * Update a user.
   * @returns UserPublic Successful Response
   * @throws ApiError
   */
  public static updateUser(
    data: UsersData["UpdateUser"],
  ): CancelablePromise<UserPublic> {
    const { userId, requestBody } = data
    return __request(OpenAPI, {
      method: "PATCH",
      url: "/api/v1/users/{user_id}",
      path: {
        user_id: userId,
      },
      body: requestBody,
      mediaType: "application/json",
      errors: {
        422: `Validation Error`,
      },
    })
  }

  /**
   * Delete User
   * Delete a user.
   * @returns Message Successful Response
   * @throws ApiError
   */
  public static deleteUser(
    data: UsersData["DeleteUser"],
  ): CancelablePromise<Message> {
    const { userId } = data
    return __request(OpenAPI, {
      method: "DELETE",
      url: "/api/v1/users/{user_id}",
      path: {
        user_id: userId,
      },
      errors: {
        422: `Validation Error`,
      },
    })
  }
}

export class ItemsService {
  /**
   * Read Items
   * Retrieve items.
   * @returns ItemsPublic Successful Response
   * @throws ApiError
   */
  public static readItems(
    data: ItemsData["ReadItems"] = {},
  ): CancelablePromise<ItemsPublic> {
    const { skip = 0, limit = 100 } = data
    return __request(OpenAPI, {
      method: "GET",
      url: "/api/v1/items/",
      query: {
        skip,
        limit,
      },
      errors: {
        422: `Validation Error`,
      },
    })
  }

  /**
   * Create Item
   * @returns ItemPublic Successful Response
   * @throws ApiError
   */
  public static createItem(
    data: ItemsData["CreateItem"],
  ): CancelablePromise<ItemPublic> {
    const { requestBody } = data
    return __request(OpenAPI, {
      method: "POST",
      url: "/api/v1/items/",
      body: requestBody,
      mediaType: "application/json",
      errors: {
        422: `Validation Error`,
      },
    })
  }

  /**
   * Read Item
   * Get item by ID.
   * @returns ItemPublic Successful Response
   * @throws ApiError
   */
  public static readItem(
    data: ItemsData["ReadItem"],
  ): CancelablePromise<ItemPublic> {
    const { id } = data
    return __request(OpenAPI, {
      method: "GET",
      url: "/api/v1/items/{id}",
      path: {
        id,
      },
      errors: {
        422: `Validation Error`,
      },
    })
  }

  /**
   * Update Item
   * Update an item.
   * @returns ItemPublic Successful Response
   * @throws ApiError
   */
  public static updateItem(
    data: ItemsData["UpdateItem"],
  ): CancelablePromise<ItemPublic> {
    const { id, requestBody } = data
    return __request(OpenAPI, {
      method: "PUT",
      url: "/api/v1/items/{id}",
      path: {
        id,
      },
      body: requestBody,
      mediaType: "application/json",
      errors: {
        422: `Validation Error`,
      },
    })
  }

  /**
   * Delete Item
   * Delete an item.
   * @returns Message Successful Response
   * @throws ApiError
   */
  public static deleteItem(
    data: ItemsData["DeleteItem"],
  ): CancelablePromise<Message> {
    const { id } = data
    return __request(OpenAPI, {
      method: "DELETE",
      url: "/api/v1/items/{id}",
      path: {
        id,
      },
      errors: {
        422: `Validation Error`,
      },
    })
  }
}

export class CartService {
  /**
   * Get Cart
   * @returns CartSchema Successful Response
   * @throws ApiError
   */
  public static getCart(): CancelablePromise<CartSchema> {
    return __request(OpenAPI, {
      method: "GET",
      url: "/api/v1/cart/",
    })
  }

  /**
   * Add To Cart
   * @returns Message Successful Response
   * @throws ApiError
   */
  public static addToCart(
    data: CartData["AddToCart"],
  ): CancelablePromise<Message> {
    const { requestBody } = data
    return __request(OpenAPI, {
      method: "POST",
      url: "/api/v1/cart/add",
      body: requestBody,
      mediaType: "application/json",
      errors: {
        422: `Validation Error`,
      },
    })
  }

  /**
   * Delete Cart Item
   * @returns Message Successful Response
   * @throws ApiError
   */
  public static deleteCartItem(
    data: CartData["DeleteCartItem"],
  ): CancelablePromise<Message> {
    const { id } = data
    return __request(OpenAPI, {
      method: "DELETE",
      url: "/api/v1/cart/{id}",
      path: {
        id,
      },
      errors: {
        422: `Validation Error`,
      },
    })
  }

  /**
   * Order Info
   * @returns OrderPublic Successful Response
   * @throws ApiError
   */
  public static orderInfo(
    data: CartData["OrderInfo"],
  ): CancelablePromise<OrderPublic> {
    const { requestBody } = data
    return __request(OpenAPI, {
      method: "POST",
      url: "/api/v1/cart/orderinfo",
      body: requestBody,
      mediaType: "application/json",
      errors: {
        422: `Validation Error`,
      },
    })
  }

  /**
   * Checkout
   * @returns string Successful Response
   * @throws ApiError
   */
  public static checkout(
    data: CartData["Checkout"],
  ): CancelablePromise<string> {
    const { orderid } = data
    return __request(OpenAPI, {
      method: "POST",
      url: "/api/v1/cart/checkout",
      query: {
        orderid,
      },
      errors: {
        422: `Validation Error`,
      },
    })
  }

  /**
   * Get Price
   * @returns PricerResponse Successful Response
   * @throws ApiError
   */
  public static getPrice(
    data: CartData["GetPrice"],
  ): CancelablePromise<PricerResponse> {
    const { requestBody } = data
    return __request(OpenAPI, {
      method: "POST",
      url: "/api/v1/cart/price",
      body: requestBody,
      mediaType: "application/json",
      errors: {
        422: `Validation Error`,
      },
    })
  }
}

export class OrdersService {
  /**
   * Get Orders
   * Retrieve orders.
   * @returns OrdersPublic Successful Response
   * @throws ApiError
   */
  public static getOrders(
    data: OrdersData["GetOrders"] = {},
  ): CancelablePromise<OrdersPublic> {
    const { skip = 0, limit = 100 } = data
    return __request(OpenAPI, {
      method: "GET",
      url: "/api/v1/orders/",
      query: {
        skip,
        limit,
      },
      errors: {
        422: `Validation Error`,
      },
    })
  }

  /**
   * Create Order
   * Create new order.
   * @returns OrderPublic Successful Response
   * @throws ApiError
   */
  public static createOrder(
    data: OrdersData["CreateOrder"],
  ): CancelablePromise<OrderPublic> {
    const { requestBody } = data
    return __request(OpenAPI, {
      method: "POST",
      url: "/api/v1/orders/",
      body: requestBody,
      mediaType: "application/json",
      errors: {
        422: `Validation Error`,
      },
    })
  }

  /**
   * Patch Order
   * Update an order.
   * @returns OrderPublic Successful Response
   * @throws ApiError
   */
  public static patchOrder(
    data: OrdersData["PatchOrder"],
  ): CancelablePromise<OrderPublic> {
    const { orderId, requestBody } = data
    return __request(OpenAPI, {
      method: "PATCH",
      url: "/api/v1/orders/{order_id}",
      path: {
        order_id: orderId,
      },
      body: requestBody,
      mediaType: "application/json",
      errors: {
        422: `Validation Error`,
      },
    })
  }

  /**
   * Delete Order
   * Delete an order.
   * @returns Message Successful Response
   * @throws ApiError
   */
  public static deleteOrder(
    data: OrdersData["DeleteOrder"],
  ): CancelablePromise<Message> {
    const { orderId } = data
    return __request(OpenAPI, {
      method: "DELETE",
      url: "/api/v1/orders/{order_id}",
      path: {
        order_id: orderId,
      },
      errors: {
        422: `Validation Error`,
      },
    })
  }
}

export class EmailService {
  /**
   * Test Email
   * @returns Message Successful Response
   * @throws ApiError
   */
  public static testEmail(
    data: EmailData["TestEmail"],
  ): CancelablePromise<Message> {
    const { emailTo } = data
    return __request(OpenAPI, {
      method: "POST",
      url: "/api/v1/email/test-email/",
      query: {
        email_to: emailTo,
      },
      errors: {
        422: `Validation Error`,
      },
    })
  }
}

export class PingService {
  /**
   * Pong
   * @returns PingResponse Successful Response
   * @throws ApiError
   */
  public static pong(): CancelablePromise<PingResponse> {
    return __request(OpenAPI, {
      method: "GET",
      url: "/api/v1/ping/",
    })
  }
}

export class DocumentsService {
  /**
   * Create Document
   * @returns DocumentPublic Successful Response
   * @throws ApiError
   */
  public static createDocument(
    data: DocumentsData["CreateDocument"],
  ): CancelablePromise<DocumentPublic> {
    const { templateName, requestBody } = data
    return __request(OpenAPI, {
      method: "POST",
      url: "/api/v1/documents/{template_name}",
      path: {
        template_name: templateName,
      },
      body: requestBody,
      mediaType: "application/json",
      errors: {
        422: `Validation Error`,
      },
    })
  }

  /**
   * Get Draft Document
   * @returns any Document found
   * @throws ApiError
   */
  public static getDraftDocument(
    data: DocumentsData["GetDraftDocument"],
  ): CancelablePromise<any> {
    const { orderId } = data
    return __request(OpenAPI, {
      method: "GET",
      url: "/api/v1/documents/draft/{order_id}",
      path: {
        order_id: orderId,
      },
      errors: {
        422: `Validation Error`,
      },
    })
  }

  /**
   * Sign Draft Document
   * @returns Message Successful Response
   * @throws ApiError
   */
  public static signDraftDocument(
    data: DocumentsData["SignDraftDocument"],
  ): CancelablePromise<Message> {
    const { orderId, requestBody } = data
    return __request(OpenAPI, {
      method: "POST",
      url: "/api/v1/documents/draft/{order_id}",
      path: {
        order_id: orderId,
      },
      body: requestBody,
      mediaType: "application/json",
      errors: {
        422: `Validation Error`,
      },
    })
  }

  /**
   * Get Documents
   * Retrieve documents.
   * @returns DocumentsPublic Successful Response
   * @throws ApiError
   */
  public static getDocuments(
    data: DocumentsData["GetDocuments"] = {},
  ): CancelablePromise<DocumentsPublic> {
    const { skip = 0, limit = 100 } = data
    return __request(OpenAPI, {
      method: "GET",
      url: "/api/v1/documents/",
      query: {
        skip,
        limit,
      },
      errors: {
        422: `Validation Error`,
      },
    })
  }

  /**
   * Patch Document
   * Update an document.
   * @returns DocumentPublic Successful Response
   * @throws ApiError
   */
  public static patchDocument(
    data: DocumentsData["PatchDocument"],
  ): CancelablePromise<DocumentPublic> {
    const { documentId, requestBody } = data
    return __request(OpenAPI, {
      method: "PATCH",
      url: "/api/v1/documents/{document_id}",
      path: {
        document_id: documentId,
      },
      body: requestBody,
      mediaType: "application/json",
      errors: {
        422: `Validation Error`,
      },
    })
  }

  /**
   * Delete Document
   * Delete an document.
   * @returns Message Successful Response
   * @throws ApiError
   */
  public static deleteDocument(
    data: DocumentsData["DeleteDocument"],
  ): CancelablePromise<Message> {
    const { documentId } = data
    return __request(OpenAPI, {
      method: "DELETE",
      url: "/api/v1/documents/{document_id}",
      path: {
        document_id: documentId,
      },
      errors: {
        422: `Validation Error`,
      },
    })
  }
}

export class TemplateService {
  /**
   * Create Document
   * @returns DocumentPublic Successful Response
   * @throws ApiError
   */
  public static createDocument(
    data: TemplateData["CreateDocument"],
  ): CancelablePromise<DocumentPublic> {
    const { templateName, requestBody } = data
    return __request(OpenAPI, {
      method: "POST",
      url: "/api/v1/documents/{template_name}",
      path: {
        template_name: templateName,
      },
      body: requestBody,
      mediaType: "application/json",
      errors: {
        422: `Validation Error`,
      },
    })
  }

  /**
   * Get Template File By Name
   * Get template by ID.
   * @returns unknown Successful Response
   * @throws ApiError
   */
  public static getTemplateFileByName(
    data: TemplateData["GetTemplateFileByName"],
  ): CancelablePromise<unknown> {
    const { templateName } = data
    return __request(OpenAPI, {
      method: "GET",
      url: "/api/v1/templates/{template_name}",
      path: {
        template_name: templateName,
      },
      errors: {
        422: `Validation Error`,
      },
    })
  }

  /**
   * Get Templates
   * Retrieve templates.
   * @returns TemplatesPublic Successful Response
   * @throws ApiError
   */
  public static getTemplates(
    data: TemplateData["GetTemplates"] = {},
  ): CancelablePromise<TemplatesPublic> {
    const { skip = 0, limit = 100 } = data
    return __request(OpenAPI, {
      method: "GET",
      url: "/api/v1/templates/",
      query: {
        skip,
        limit,
      },
      errors: {
        422: `Validation Error`,
      },
    })
  }

  /**
   * Create Template
   * Create new template.
   * @returns Message Successful Response
   * @throws ApiError
   */
  public static createTemplate(
    data: TemplateData["CreateTemplate"],
  ): CancelablePromise<Message> {
    const { formData } = data
    return __request(OpenAPI, {
      method: "POST",
      url: "/api/v1/templates/",
      formData: formData,
      mediaType: "multipart/form-data",
      errors: {
        422: `Validation Error`,
      },
    })
  }

  /**
   * Patch Template
   * Update an template.
   * @returns Message Successful Response
   * @throws ApiError
   */
  public static patchTemplate(
    data: TemplateData["PatchTemplate"],
  ): CancelablePromise<Message> {
    const { templateId, formData } = data
    return __request(OpenAPI, {
      method: "PATCH",
      url: "/api/v1/templates/{template_id}",
      path: {
        template_id: templateId,
      },
      formData: formData,
      mediaType: "multipart/form-data",
      errors: {
        422: `Validation Error`,
      },
    })
  }

  /**
   * Delete Template
   * Delete an template.
   * @returns Message Successful Response
   * @throws ApiError
   */
  public static deleteTemplate(
    data: TemplateData["DeleteTemplate"],
  ): CancelablePromise<Message> {
    const { templateId } = data
    return __request(OpenAPI, {
      method: "DELETE",
      url: "/api/v1/templates/{template_id}",
      path: {
        template_id: templateId,
      },
      errors: {
        422: `Validation Error`,
      },
    })
  }

  /**
   * Create Template Link
   * Link template to item.
   * @returns Message Successful Response
   * @throws ApiError
   */
  public static createTemplateLink(
    data: TemplateData["CreateTemplateLink"],
  ): CancelablePromise<Message> {
    const { formData } = data
    return __request(OpenAPI, {
      method: "POST",
      url: "/api/v1/templates/link",
      formData: formData,
      mediaType: "application/x-www-form-urlencoded",
      errors: {
        422: `Validation Error`,
      },
    })
  }
}

export class DocumentService {
  /**
   * Get Draft Document
   * @returns any Document found
   * @throws ApiError
   */
  public static getDraftDocument(
    data: DocumentData["GetDraftDocument"],
  ): CancelablePromise<any> {
    const { orderId } = data
    return __request(OpenAPI, {
      method: "GET",
      url: "/api/v1/documents/draft/{order_id}",
      path: {
        order_id: orderId,
      },
      errors: {
        422: `Validation Error`,
      },
    })
  }

  /**
   * Sign Draft Document
   * @returns Message Successful Response
   * @throws ApiError
   */
  public static signDraftDocument(
    data: DocumentData["SignDraftDocument"],
  ): CancelablePromise<Message> {
    const { orderId, requestBody } = data
    return __request(OpenAPI, {
      method: "POST",
      url: "/api/v1/documents/draft/{order_id}",
      path: {
        order_id: orderId,
      },
      body: requestBody,
      mediaType: "application/json",
      errors: {
        422: `Validation Error`,
      },
    })
  }

  /**
   * Get Documents
   * Retrieve documents.
   * @returns DocumentsPublic Successful Response
   * @throws ApiError
   */
  public static getDocuments(
    data: DocumentData["GetDocuments"] = {},
  ): CancelablePromise<DocumentsPublic> {
    const { skip = 0, limit = 100 } = data
    return __request(OpenAPI, {
      method: "GET",
      url: "/api/v1/documents/",
      query: {
        skip,
        limit,
      },
      errors: {
        422: `Validation Error`,
      },
    })
  }

  /**
   * Patch Document
   * Update an document.
   * @returns DocumentPublic Successful Response
   * @throws ApiError
   */
  public static patchDocument(
    data: DocumentData["PatchDocument"],
  ): CancelablePromise<DocumentPublic> {
    const { documentId, requestBody } = data
    return __request(OpenAPI, {
      method: "PATCH",
      url: "/api/v1/documents/{document_id}",
      path: {
        document_id: documentId,
      },
      body: requestBody,
      mediaType: "application/json",
      errors: {
        422: `Validation Error`,
      },
    })
  }

  /**
   * Delete Document
   * Delete an document.
   * @returns Message Successful Response
   * @throws ApiError
   */
  public static deleteDocument(
    data: DocumentData["DeleteDocument"],
  ): CancelablePromise<Message> {
    const { documentId } = data
    return __request(OpenAPI, {
      method: "DELETE",
      url: "/api/v1/documents/{document_id}",
      path: {
        document_id: documentId,
      },
      errors: {
        422: `Validation Error`,
      },
    })
  }
}

export class TemplatesService {
  /**
   * Get Template File By Name
   * Get template by ID.
   * @returns unknown Successful Response
   * @throws ApiError
   */
  public static getTemplateFileByName(
    data: TemplatesData["GetTemplateFileByName"],
  ): CancelablePromise<unknown> {
    const { templateName } = data
    return __request(OpenAPI, {
      method: "GET",
      url: "/api/v1/templates/{template_name}",
      path: {
        template_name: templateName,
      },
      errors: {
        422: `Validation Error`,
      },
    })
  }

  /**
   * Get Templates
   * Retrieve templates.
   * @returns TemplatesPublic Successful Response
   * @throws ApiError
   */
  public static getTemplates(
    data: TemplatesData["GetTemplates"] = {},
  ): CancelablePromise<TemplatesPublic> {
    const { skip = 0, limit = 100 } = data
    return __request(OpenAPI, {
      method: "GET",
      url: "/api/v1/templates/",
      query: {
        skip,
        limit,
      },
      errors: {
        422: `Validation Error`,
      },
    })
  }

  /**
   * Create Template
   * Create new template.
   * @returns Message Successful Response
   * @throws ApiError
   */
  public static createTemplate(
    data: TemplatesData["CreateTemplate"],
  ): CancelablePromise<Message> {
    const { formData } = data
    return __request(OpenAPI, {
      method: "POST",
      url: "/api/v1/templates/",
      formData: formData,
      mediaType: "multipart/form-data",
      errors: {
        422: `Validation Error`,
      },
    })
  }

  /**
   * Patch Template
   * Update an template.
   * @returns Message Successful Response
   * @throws ApiError
   */
  public static patchTemplate(
    data: TemplatesData["PatchTemplate"],
  ): CancelablePromise<Message> {
    const { templateId, formData } = data
    return __request(OpenAPI, {
      method: "PATCH",
      url: "/api/v1/templates/{template_id}",
      path: {
        template_id: templateId,
      },
      formData: formData,
      mediaType: "multipart/form-data",
      errors: {
        422: `Validation Error`,
      },
    })
  }

  /**
   * Delete Template
   * Delete an template.
   * @returns Message Successful Response
   * @throws ApiError
   */
  public static deleteTemplate(
    data: TemplatesData["DeleteTemplate"],
  ): CancelablePromise<Message> {
    const { templateId } = data
    return __request(OpenAPI, {
      method: "DELETE",
      url: "/api/v1/templates/{template_id}",
      path: {
        template_id: templateId,
      },
      errors: {
        422: `Validation Error`,
      },
    })
  }

  /**
   * Create Template Link
   * Link template to item.
   * @returns Message Successful Response
   * @throws ApiError
   */
  public static createTemplateLink(
    data: TemplatesData["CreateTemplateLink"],
  ): CancelablePromise<Message> {
    const { formData } = data
    return __request(OpenAPI, {
      method: "POST",
      url: "/api/v1/templates/link",
      formData: formData,
      mediaType: "application/x-www-form-urlencoded",
      errors: {
        422: `Validation Error`,
      },
    })
  }
}

export class PaymentsService {
  /**
   * Success
   * @returns unknown Successful Response
   * @throws ApiError
   */
  public static success(
    data: PaymentsData["Success"],
  ): CancelablePromise<unknown> {
    const { sessionId } = data
    return __request(OpenAPI, {
      method: "GET",
      url: "/api/v1/payments/success",
      query: {
        session_id: sessionId,
      },
      errors: {
        422: `Validation Error`,
      },
    })
  }

  /**
   * Cancel
   * @returns unknown Successful Response
   * @throws ApiError
   */
  public static cancel(
    data: PaymentsData["Cancel"],
  ): CancelablePromise<unknown> {
    const { sessionId } = data
    return __request(OpenAPI, {
      method: "GET",
      url: "/api/v1/payments/cancel",
      query: {
        session_id: sessionId,
      },
      errors: {
        422: `Validation Error`,
      },
    })
  }

  /**
   * Stripe Webhook
   * @returns unknown Successful Response
   * @throws ApiError
   */
  public static stripeWebhook(): CancelablePromise<unknown> {
    return __request(OpenAPI, {
      method: "POST",
      url: "/api/v1/payments/stripe-webhook",
    })
  }
}

export class PaymentService {
  /**
   * Success
   * @returns unknown Successful Response
   * @throws ApiError
   */
  public static success(
    data: PaymentData["Success"],
  ): CancelablePromise<unknown> {
    const { sessionId } = data
    return __request(OpenAPI, {
      method: "GET",
      url: "/api/v1/payments/success",
      query: {
        session_id: sessionId,
      },
      errors: {
        422: `Validation Error`,
      },
    })
  }

  /**
   * Cancel
   * @returns unknown Successful Response
   * @throws ApiError
   */
  public static cancel(
    data: PaymentData["Cancel"],
  ): CancelablePromise<unknown> {
    const { sessionId } = data
    return __request(OpenAPI, {
      method: "GET",
      url: "/api/v1/payments/cancel",
      query: {
        session_id: sessionId,
      },
      errors: {
        422: `Validation Error`,
      },
    })
  }

  /**
   * Stripe Webhook
   * @returns unknown Successful Response
   * @throws ApiError
   */
  public static stripeWebhook(): CancelablePromise<unknown> {
    return __request(OpenAPI, {
      method: "POST",
      url: "/api/v1/payments/stripe-webhook",
    })
  }
}
